import { Box, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { adminApiService } from "../../../utils/api";
import {
  Coordinate,
  GfFlyerItemCategory,
  GfFlyerItemComponentPosition,
  GfStore,
  GfFigmaTemplateData,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";
import { GridFilterModel, GridPaginationModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { GridSortModel } from "@mui/x-data-grid";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { TableWrapper } from "./styled";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useFigmaComponentLayoutTableColumns } from "./useFigmaComponentLayoutTableColumns";
import EditTemplateModal from "./EditTemplateModal";
import BatchEditModal from "./BatchEditModal";
import { CoordinatesModal } from "./CoordinatesModal";
import { FilterBar } from "./FilterBar";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { AutocompleteOption } from "../../components/CustomAutocomplete";
import { FrontPage } from "../../../utils/constant/stringConstant";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { ButtonWithTooltip } from "../../components/ButtonWithTooltip";
import { Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

const FigmaComponentLayout = () => {
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();
  const [componentPositions, setComponentPositions] =
    useState<TableData<GfFlyerItemComponentPosition>>();

  const [isDataLoading, setIsDataLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const { paginationModel, sortModel, handleTableChange } =
    useQueryParams(100);

  const [isCoordinatesModalOpen, setIsCoordinatesModalOpen] = useState(false);
  const [coordinatesData, setCoordinatesData] = useState<Coordinate[]>([]);
  const [selectedComponentName, setSelectedComponentName] = useState("");
  const [selectedComponentPositionId, setSelectedComponentPositionId] =
    useState<string>("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<
    GfFlyerItemComponentPosition | undefined
  >(undefined);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<
    GfFlyerItemComponentPosition | undefined
  >(undefined);

  const [isBatchEditModalOpen, setIsBatchEditModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [selectedItems, setSelectedItems] = useState<
    GfFlyerItemComponentPosition[]
  >([]);

  const [stores, setStores] = useState<GfStore[]>([]);
  const [departments, setDepartments] = useState<GfFlyerItemCategory[]>([]);
  const [templates, setTemplates] = useState<GfFigmaTemplateData[]>([]);
  const [selectedStores, setSelectedStores] = useState<GfStore[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<
    GfFlyerItemCategory[]
  >([]);
  const [selectedTemplates, setSelectedTemplates] = useState<
    GfFigmaTemplateData[]
  >([]);
  const [countValue, setCountValue] = useState("");
  const [isStoreLoading, setIsStoreLoading] = useState(false);
  const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);

  const displayCoordinatesModal = (item: GfFlyerItemComponentPosition) => {
    setCoordinatesData(item.coordinates || []);
    setSelectedComponentPositionId(item.id);
    setSelectedComponentName(
      `${item.store?.merchant?.name || ""}-${item.store?.signature || ""} ${
        item.figmaTemplateData?.name || ""
      } | ${item.flyerItemCategory?.name || "Front-Page"} | ${
        item.flyerItemCount || ""
      } | (${item.id || ""})`
    );
    setIsCoordinatesModalOpen(true);
  };

  const tableColumns = useFigmaComponentLayoutTableColumns(
    displayCoordinatesModal,
    (row) => {
      setSelectedRow(row);
      setIsEditModalOpen(true);
    },
    (row) => {
      handleOpenDelete(row);
    }
  );

  const fetchStores = async (searchText = "a") => {
    try {
      setIsStoreLoading(true);
      const response = await adminApiService.gfStoreAdminControllerSearch(
        searchText,
        1,
        100
      );
      setStores(response.data || []);
      setIsStoreLoading(false);
    } catch (error) {
      console.error("Error fetching stores:", error);
      setIsStoreLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setIsDepartmentLoading(true);
      const response =
        await adminApiService.getManyBaseGfFlyerItemCategoryAdminControllerGfFlyerItemCategory();
      const frontPageOption = {
        id: FrontPage,
        name: FrontPage,
      } as GfFlyerItemCategory;
      setDepartments([frontPageOption, ...(response.data || [])]);
      setIsDepartmentLoading(false);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setIsDepartmentLoading(false);
    }
  };

  const fetchTemplates = async (searchText = "") => {
    try {
      setIsTemplateLoading(true);
      const response =
        await adminApiService.gfFigmaTemplateDataAdminControllerSearch(
          searchText,
          1,
          100
        );
      setTemplates(response.results || []);
      setIsTemplateLoading(false);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setIsTemplateLoading(false);
    }
  };

  const fetchComponentPositionsWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setIsDataLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfFlyerItemComponentPositionsAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setComponentPositions((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
      (error) => {
        console.error("Error fetching component positions:", error);
        toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const refreshComponentPositions = () => {
    if (muiFilterModelHasActiveItems(filterModel)) {
      fetchComponentPositionsWithFilter(filterModel);
    } else {
      fetchComponentPositions(paginationModel, sortModel);
    }
  };

  const fetchComponentPositions = async (
    paginationModel: GridPaginationModel,
    gridSortModel: GridSortModel
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setIsDataLoading,
      async () => {
        const filters = [];
        const orConditions = [];

        if (selectedStores.length > 0) {
          const storeIds = selectedStores.map((store) => store.id).join(",");
          filters.push(`store.id||in||${storeIds}`);
          orConditions.push(`figmaStores.id||in||${storeIds}`);
        }

        const regularDepartments = selectedDepartments.filter(
          (dept) => dept.id !== FrontPage
        );
        const hasFrontPage = selectedDepartments.some(
          (dept) => dept.id === FrontPage
        );

        if (regularDepartments.length > 0) {
          filters.push(
            `flyerItemCategory.id||in||${regularDepartments
              .map((dept) => dept.id)
              .join(",")}`
          );
        }

        if (hasFrontPage) {
          filters.push(`frontPage||eq||true`);
        }

        if (selectedTemplates.length > 0) {
          filters.push(
            `figmaTemplateData.id||in||${selectedTemplates
              .map((template) => template.id)
              .join(",")}`
          );
        }

        if (countValue) {
          filters.push(`flyerItemCount||eq||${countValue}`);
        }

        return adminApiService.getManyBaseGfFlyerItemComponentPositionsAdminControllerGfFlyerItemComponentPosition(
          undefined,
          undefined,
          filters.length > 0 ? filters : undefined,
          orConditions.length > 0 ? orConditions : undefined,
          gridSortModel?.length
            ? [
                `${
                  gridSortModel[0].field
                },${gridSortModel[0]?.sort?.toUpperCase()}`,
              ]
            : [`updatedAt,DESC`],
          [
            "store",
            "store.merchant",
            "flyerItemCategory",
            "figmaTemplateData",
            "figmaTemplateData.stores",
            "figmaTemplateData.stores.merchant",
          ],
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined
        );
      },
      (componentData) => {
        setComponentPositions((prev) => ({
          ...prev,
          rows: componentData.data,
          total: componentData?.total,
        }));
      },
      (error) => {
        console.error("Error fetching component positions:", error);
        toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
      },
    );
  };

  const handleStoreChange = (stores: GfStore[] | undefined) => {
    setSelectedStores(stores || []);
    if (stores && stores.length > 0) {
      setSelectedTemplates([]);
    }
  };

  const handleDepartmentChange = (
    departments: GfFlyerItemCategory[] | undefined
  ) => {
    setSelectedDepartments(departments || []);
  };

  const handleTemplateChange = (
    templates: GfFigmaTemplateData[] | undefined
  ) => {
    setSelectedTemplates(templates || []);
    if (templates && templates.length > 0) {
      setSelectedStores([]);
    }
  };

  const handleCountChange = (count: string) => {
    setCountValue(count);
  };

  const handleOpenDelete = (item: GfFlyerItemComponentPosition) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const handleCancel = () => {
    setIsDeleteModalOpen(false);
    setItemToDelete(undefined);
  };

  const handleOnDelete = async () => {
    if (!itemToDelete && selectedItems.length === 0) return;

    try {
      setIsDataLoading(true);
      if (itemToDelete) {
        await adminApiService.deleteOneBaseGfFlyerItemComponentPositionsAdminControllerGfFlyerItemComponentPosition(
          itemToDelete.id,
          authHeaders
        );
      } else {
        const promises = selectedItems.map((item) =>
          adminApiService.deleteOneBaseGfFlyerItemComponentPositionsAdminControllerGfFlyerItemComponentPosition(
            item.id,
            authHeaders
          )
        );
        await Promise.all(promises);
      }
      toastSuccess(t(messages.Successfully_Deleted()));
      setSelectedRows([]);
      setSelectedItems([]);
      refreshComponentPositions();
      handleCancel();
    } catch (error) {
      console.error("Error deleting component position:", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleStoreSearch = (value: string) => {
    fetchStores(value);
  };

  const handleTemplateSearch = (value: string) => {
    fetchTemplates(value);
  };

  const handleRowSelection = (rowSelectionModel: GridRowSelectionModel) => {
    setSelectedRows(rowSelectionModel);

    if (componentPositions?.rows) {
      const items = componentPositions.rows.filter((row) =>
        rowSelectionModel.includes(row.id)
      );
      setSelectedItems(items);
    }
  };

  const handleBatchEdit = () => {
    if (selectedItems.length === 0) {
      toastError("Please select items to edit");
      return;
    }
    setIsBatchEditModalOpen(true);
  };

  const handleBatchDelete = () => {
    if (selectedItems.length === 0) {
      toastError("Please select items to delete");
      return;
    }
    setIsDeleteModalOpen(true);
  };

  const storeOptions: AutocompleteOption<GfStore>[] = stores.map((store) => ({
    value: store,
    label: makeStoreDisplayName(store),
  }));

  const departmentOptions: AutocompleteOption<GfFlyerItemCategory>[] =
    departments.map((dept) => ({
      value: dept,
      label: dept.name,
    }));

  const templateOptions: AutocompleteOption<GfFigmaTemplateData>[] =
    templates.map((template) => ({
      value: template,
      label: template.name,
    }));

  const selectedStoreOptions: AutocompleteOption<GfStore>[] =
    selectedStores.map((store) => ({
      value: store,
      label: makeStoreDisplayName(store),
    }));

  const selectedDepartmentOptions: AutocompleteOption<GfFlyerItemCategory>[] =
    selectedDepartments.map((dept) => ({
      value: dept,
      label: dept.name,
    }));

  const selectedTemplateOptions: AutocompleteOption<GfFigmaTemplateData>[] =
    selectedTemplates.map((template) => ({
      value: template,
      label: template.name,
    }));

  useEffect(() => {
    fetchDepartments();
    fetchTemplates();
    fetchStores();
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchComponentPositionsWithFilter(filterModel);
    } else {
      fetchComponentPositions(paginationModel, sortModel);
    }
  }, [
    paginationModel,
    sortModel,
    selectedStores,
    selectedDepartments,
    selectedTemplates,
    countValue,
    filterModel,
  ]);

  return (
    <SetupPageContainer>
      <AccountCardWrapper
        title={t(messages.Figma_Component_Layout()) as string}
      >
        <Box mt={2}>
          <FilterBar
            storeOptions={storeOptions}
            departmentOptions={departmentOptions}
            templateOptions={templateOptions}
            selectedStores={selectedStoreOptions}
            selectedDepartments={selectedDepartmentOptions}
            selectedTemplates={selectedTemplateOptions}
            countValue={countValue}
            onStoreChange={handleStoreChange}
            onDepartmentChange={handleDepartmentChange}
            onTemplateChange={handleTemplateChange}
            onCountChange={handleCountChange}
            isStoreLoading={isStoreLoading}
            isDepartmentLoading={isDepartmentLoading}
            isTemplateLoading={isTemplateLoading}
            onStoreSearch={handleStoreSearch}
            onTemplateSearch={handleTemplateSearch}
          />

          <Box
            sx={{
              mb: 2,
              ml: "20px",
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <Stack direction="row" spacing={2}>
              <Tooltip
                title={
                  selectedRows.length <= 1
                    ? t(messages.Batch_Edit_Disabled_Tooltip())
                    : ""
                }
                arrow
                placement="left"
                disableHoverListener={selectedRows.length > 1}
              >
                <span>
                  <ButtonWithTooltip
                    variant="primary"
                    size="medium"
                    onClick={handleBatchEdit}
                    data-testid="batch-edit-button"
                    tooltipText={t(messages.Batch_Edit_Enabled_Tooltip())}
                    tooltipPlacement="auto"
                    showInfoIcon={true}
                    disabled={selectedRows.length <= 1}
                  >
                    {t(messages.Batch_Edit_With_Count(), {
                      count: selectedRows.length,
                    })}
                  </ButtonWithTooltip>
                </span>
              </Tooltip>
              <BlackFontTransparentButton
                variant="text"
                startIcon={
                  <DeleteIcon
                    style={{
                      color: "#ff8038",
                      fontSize: 25,
                      opacity: !selectedRows.length ? 0.5 : 1,
                    }}
                  />
                }
                disabled={!selectedRows.length}
                onClick={handleBatchDelete}
                data-testid="batch-delete-button"
                sx={{ textTransform: "capitalize" }}
              >
                {t(messages.Delete())}
              </BlackFontTransparentButton>
            </Stack>
          </Box>

          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              columns={tableColumns}
              rows={componentPositions?.rows}
              total={isDataLoading ? 0 : componentPositions?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={isDataLoading}
              handleRowSelection={handleRowSelection}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>

          <CoordinatesModal
            open={isCoordinatesModalOpen}
            onClose={() => setIsCoordinatesModalOpen(false)}
            coordinates={coordinatesData}
            itemName={selectedComponentName}
            componentPositionId={selectedComponentPositionId}
            onUpdated={() => {
              refreshComponentPositions();
              setIsCoordinatesModalOpen(false);
            }}
          />

          <EditTemplateModal
            open={isEditModalOpen}
            item={selectedRow}
            onClose={() => setIsEditModalOpen(false)}
            onUpdated={() => refreshComponentPositions()}
          />

          <BatchEditModal
            open={isBatchEditModalOpen}
            items={selectedItems}
            onClose={() => setIsBatchEditModalOpen(false)}
            onUpdated={() => {
              refreshComponentPositions();
              setSelectedRows([]);
              setSelectedItems([]);
            }}
          />

          <MuiConfirmationModal
            open={isDeleteModalOpen}
            handleCancel={handleCancel}
            handleConfirm={handleOnDelete}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
              messages.Selected_Items(),
              { count: itemToDelete ? 1 : selectedItems.length }
            )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
          />
        </Box>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default FigmaComponentLayout;
